import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AlertTriangle, CalendarClock } from 'lucide-react';

import GenericSelect from '../../../components/ui/GenericSelect';
import Button from '../../../components/ui/Button';
import PanelCard from '../../performance-operations/components/PanelCard';
import {
  Badge, Footer, KpiSub, KpiValue, PageEmpty, ProgressFill, ProgressTrack, State, Sub,
} from '../../performance-operations/styles';
import { formatNumber, usePanel } from '../../performance-operations/config';
import { KIND_META } from '../../performance-inbox/config';
import AcknowledgementBanner from '../../my-period-results/components/AcknowledgementBanner';
import FinalLevelCard from '../../my-period-results/components/FinalLevelCard';
import { formatDate, formatDateTime } from '../../../utils/Utils';
import {
  Actions, Explain, Field, Highlight, HighlightTitle, InfoLine, ListRow, MetaRow, NextDue, ProgressLine, RowButton, RowHead,
  RowMain, RowTitle, ScoreRow, ScoreValue, SectionLabel, SummaryGrid, TopBar, Warn,
} from '../styles';
import {
  DEVELOPMENT_PLANS_PATH, GOALS_PREVIEW, acknowledgeResult, dueText, fetchMe, hasScore, meLink, stageLabel, taskLabel,
} from '../config';

// PM Analitik Faz 2 (spec §8.2) — E18 "Performansım" Özet sekmesi. E17 "Kendi Performansım" sekmesi AYNI bileşeni aynı `me`
// ucuyla çizer (spec §9.4): yönetici/İK yetkisi kendi sonucunu erken görmeyi sağlamaz — sonuç kartı yalnız yayımlanmış sonucu
// gösterir (görünürlük sunucuda, IMyPeriodResultsService). Tek istek; panellerin yükleme/hata/Tekrar Dene durumu ortak.
// `periodId` / `onPeriodChange`: dönem seçiminin sahibi çağırandır (URL). `onOpen(tab, {period, goal})`: sekme geçişi; verilmezse
// Performansım sayfasının ilgili sekmesine gidilir. Görev bağlantıları sunucudan gelir ve kayda özgüdür (2026-10-28 — "Devam Et"
// formun kendisini açar); bağlantısı olmayan görev Gelen Kutum kuralıyla aynı biçimde tıklanamaz satır olarak çizilir.
export default function MeSummary({ periodId = null, onPeriodChange, onOpen }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const panel = usePanel(() => fetchMe(periodId), [periodId || '']);
  const data = panel.data;
  const common = { loading: panel.loading, error: panel.error, onRetry: panel.retry };

  const open = useCallback((tab, extra = {}) => {
    if (onOpen) onOpen(tab, extra);
    else history.push(meLink({ tab, ...extra }));
  }, [onOpen, history]);
  const go = useCallback((link) => { if (link) history.push(link); }, [history]);

  const selectedPeriodId = data?.period?.id || periodId;
  const handleAcknowledge = useCallback(async (note) => {
    if (!selectedPeriodId) return false;
    const ok = await acknowledgeResult(selectedPeriodId, note);
    if (ok) panel.retry();
    return ok;
  }, [selectedPeriodId, panel]);

  if (!panel.loading && !panel.error && data && !data.period) {
    return <PageEmpty data-state="no-period">{t('performance.workspace.me.noPeriod')}</PageEmpty>;
  }

  const kpis = data?.kpis || {};
  const goalAchievement = kpis.goalAchievement || {};
  const development = kpis.developmentActions || null;
  // TD modülü yoksa gelişim kartı hiç çizilmez (sunucu null döner); yüklenirken yer tutulur.
  const showDevelopment = panel.loading || !!development;
  const kpiSpan = showDevelopment ? 3 : 4;
  const primary = data?.primaryTask || null;
  const otherTasks = data?.otherTasks || [];
  const goals = data?.goals || [];
  const result = data?.result || null;
  const periodOptions = (data?.periods || []).map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.period.status.${p.status}`, { defaultValue: p.status })})`,
  }));
  const nextDueOverdue = !!data?.nextDueAt && new Date(data.nextDueAt).getTime() < Date.now();

  return (
    <div data-summary="me">
      <TopBar>
        <Field $w="300px">
          <GenericSelect
            label={t('performance.workspace.me.period')}
            value={selectedPeriodId}
            onChange={(v) => { if (v && onPeriodChange) onPeriodChange(v); }}
            options={periodOptions}
            idKey="id"
            labelKey="name"
            clearable={false}
          />
        </Field>
        {data?.nextDueAt ? (
          <NextDue $overdue={nextDueOverdue} data-next-due={data.nextDueSource}>
            <CalendarClock size={16} aria-hidden="true" />
            <span>
              {data.nextDueSource === 'window' ? t('performance.workspace.me.windowEnd') : t('performance.workspace.me.nextDue')}
              {': '}
              <strong>{formatDate(data.nextDueAt, lang)}</strong>
            </span>
          </NextDue>
        ) : null}
      </TopBar>

      <SummaryGrid>
        <PanelCard panelKey="kpi-openTasks" title={t('performance.workspace.me.kpi.openTasks')} span={kpiSpan} compact {...common}>
          <KpiValue data-kpi="openTasks">{formatNumber(kpis.openMandatoryTasks ?? 0, lang, 0)}</KpiValue>
          <KpiSub>{t('performance.workspace.me.kpi.openTasksSub')}</KpiSub>
        </PanelCard>

        <PanelCard panelKey="kpi-goalAchievement" title={t('performance.workspace.me.kpi.goalAchievement')} span={kpiSpan} compact {...common}>
          <KpiValue data-kpi="goalAchievement">
            {goalAchievement.value != null ? `${formatNumber(goalAchievement.value, lang)}%` : t('performance.operations.kpi.noData')}
          </KpiValue>
          <KpiSub data-goal-source={goalAchievement.source}>
            {goalAchievement.source === 'evaluated' ? t('performance.workspace.me.kpi.evaluated') : t('performance.workspace.me.kpi.live')}
          </KpiSub>
          <KpiSub data-coverage="1">
            {goalAchievement.goalsTotal > 0
              ? t('performance.workspace.me.kpi.coverage', { withData: goalAchievement.goalsWithData, total: goalAchievement.goalsTotal })
              : t('performance.workspace.me.kpi.noCommittedGoals')}
          </KpiSub>
        </PanelCard>

        {showDevelopment ? (
          <PanelCard panelKey="kpi-development" title={t('performance.workspace.me.kpi.development')} span={kpiSpan} compact {...common}>
            <KpiValue data-kpi="development">
              {development?.value != null ? `${formatNumber(development.value, lang)}%` : t('performance.operations.kpi.noData')}
            </KpiValue>
            <KpiSub>
              {development?.hasActivePlan
                ? t('performance.workspace.me.kpi.developmentRatio', { completed: development.completed, total: development.total })
                : t('performance.workspace.me.kpi.noActivePlan')}
            </KpiSub>
          </PanelCard>
        ) : null}

        <PanelCard panelKey="kpi-lastMeeting" title={t('performance.workspace.me.kpi.lastMeeting')} span={kpiSpan} compact {...common}>
          <KpiValue data-kpi="lastMeeting">
            {kpis.lastMeetingAt ? formatDate(kpis.lastMeetingAt, lang) : t('performance.workspace.me.kpi.noMeeting')}
          </KpiValue>
        </PanelCard>

        <PanelCard
          panelKey="primaryTask" title={t('performance.workspace.me.primary.title')} span={7} {...common}
          empty={!primary && otherTasks.length === 0} emptyText={t('performance.workspace.me.primary.empty')}
        >
          {primary ? (
            <Highlight $overdue={primary.overdue} data-primary={primary.taskKey}>
              <HighlightTitle>{taskLabel(t, primary.taskKey)}</HighlightTitle>
              <MetaRow>
                <Badge $tone={primary.status === 'returned' ? 'danger' : 'info'} data-primary-status={primary.status}>
                  {t(`performance.operations.taskStatus.${primary.status}`, { defaultValue: primary.status })}
                </Badge>
                <Sub>{stageLabel(t, primary.stageKey)}</Sub>
                {primary.dueAt ? (
                  <Badge $tone={primary.overdue ? 'danger' : undefined}>
                    {`${t('performance.workspace.me.primary.due', { date: formatDate(primary.dueAt, lang) })} · ${dueText(t, primary)}`}
                  </Badge>
                ) : null}
              </MetaRow>
              {primary.link ? (
                <div>
                  <Button onClick={() => go(primary.link)} data-action="continue">{t('performance.workspace.me.primary.continue')}</Button>
                </div>
              ) : null}
            </Highlight>
          ) : null}
          {otherTasks.length > 0 ? (
            <>
              <SectionLabel>{t('performance.workspace.me.primary.others')}</SectionLabel>
              {otherTasks.map((item, index) => {
                const meta = KIND_META[item.kind] || {};
                const link = item.link || null;
                const subtitle = meta.subtitle ? meta.subtitle(item, t) : item.subtitle;
                const content = (
                  <>
                    <RowTitle>{`${t(`performance.inbox.kind.${item.kind}`, { defaultValue: item.kind })} · ${item.title}`}</RowTitle>
                    {subtitle ? <Sub>{subtitle}</Sub> : null}
                  </>
                );
                return (
                  <ListRow key={`${item.kind}-${index}`} data-other={item.kind}>
                    {link
                      ? <RowButton onClick={() => go(link)}>{content}</RowButton>
                      : <RowMain data-static="1">{content}</RowMain>}
                    {item.dueAt ? <Badge $tone={item.overdue ? 'danger' : undefined}>{formatDate(item.dueAt, lang)}</Badge> : null}
                  </ListRow>
                );
              })}
            </>
          ) : null}
          <Footer>
            <Button variant="ghost" onClick={() => open('tasks')} data-action="all-tasks">{t('performance.operations.panel.seeAllPlain')}</Button>
          </Footer>
        </PanelCard>

        <PanelCard
          panelKey="goals" title={t('performance.workspace.me.goals.title')} span={5} {...common}
          empty={goals.length === 0} emptyText={t('performance.workspace.me.goals.empty')}
        >
          {goals.slice(0, GOALS_PREVIEW).map((g) => (
            <ListRow key={g.id} data-goal={g.id}>
              <RowButton onClick={() => open('goals', { goal: g.id, period: selectedPeriodId })}>
                <RowHead>
                  <RowTitle title={g.title}>{g.title}</RowTitle>
                  {g.weightPct != null ? <Sub>{t('performance.workspace.me.goals.weight', { value: formatNumber(g.weightPct, lang) })}</Sub> : null}
                </RowHead>
                <ProgressLine>
                  <ProgressTrack><ProgressFill $pct={g.progressPct ?? 0} /></ProgressTrack>
                  <Sub>{g.progressPct != null ? `${formatNumber(g.progressPct, lang)}%` : t('performance.operations.kpi.noData')}</Sub>
                </ProgressLine>
                <MetaRow>
                  <Badge>{t(`performance.goal.status.${g.status}`, { defaultValue: g.status })}</Badge>
                  <Sub>{t('performance.workspace.me.goals.lastUpdate', { date: formatDate(g.lastProgressAt, lang) })}</Sub>
                  {g.isStale ? (
                    <Warn data-stale="1"><AlertTriangle size={12} aria-hidden="true" />{t('performance.workspace.me.goals.stale')}</Warn>
                  ) : null}
                </MetaRow>
              </RowButton>
            </ListRow>
          ))}
          {goals.length > GOALS_PREVIEW ? (
            <Footer>
              <Button variant="ghost" onClick={() => open('goals', { period: selectedPeriodId })}>
                {t('performance.operations.panel.seeAll', { count: goals.length })}
              </Button>
            </Footer>
          ) : null}
        </PanelCard>

        <PanelCard panelKey="meetings" title={t('performance.workspace.me.meetings.title')} span={6} {...common}>
          <InfoLine data-next-meeting={data?.nextMeetingAt ? '1' : '0'}>
            <CalendarClock size={16} aria-hidden="true" />
            {data?.nextMeetingAt
              ? t('performance.workspace.me.meetings.next', { date: formatDateTime(data.nextMeetingAt, lang) })
              : t('performance.workspace.me.meetings.noNext')}
          </InfoLine>
          <Footer>
            <Button variant="ghost" onClick={() => open('meetings')}>{t('performance.workspace.me.meetings.open')}</Button>
          </Footer>
          {data?.developmentActions ? (
            <div data-section="development">
              <SectionLabel>{t('performance.workspace.me.meetings.developmentTitle')}</SectionLabel>
              {data.developmentActions.length === 0 ? <Sub>{t('performance.workspace.me.meetings.noDevelopment')}</Sub> : null}
              {data.developmentActions.map((a) => (
                <ListRow key={a.id} data-dev={a.id}>
                  <RowMain>
                    <RowTitle title={a.title}>{a.title}</RowTitle>
                    <Sub>{t(`performance.idp.actionStatus.${a.status}`, { defaultValue: a.status })}</Sub>
                  </RowMain>
                  {a.targetDate ? <Badge>{formatDate(a.targetDate, lang)}</Badge> : null}
                </ListRow>
              ))}
              <Footer>
                <Button variant="ghost" onClick={() => go(DEVELOPMENT_PLANS_PATH)}>{t('performance.workspace.me.meetings.openPlans')}</Button>
              </Footer>
            </div>
          ) : null}
        </PanelCard>

        <PanelCard panelKey="result" title={t('performance.workspace.me.result.title')} span={6} {...common}>
          {result && !result.published ? (
            <State data-state="unpublished">
              <span>{t('performance.workspace.me.result.unpublished')}</span>
              {!result.scoringEnabled ? <Sub>{t('performance.workspace.me.result.noScoring')}</Sub> : null}
            </State>
          ) : null}
          {result?.published ? (
            <div data-state="published">
              <FinalLevelCard finalLevelLabel={result.finalLevelLabel} />
              {hasScore(result) ? (
                <>
                  <ScoreRow>
                    {result.systemAScore != null ? (
                      <div data-score="goal">
                        <Sub>{t('performance.workspace.me.result.goalScore')}</Sub>
                        <ScoreValue>{`${formatNumber(result.systemAScore, lang)} / 100`}</ScoreValue>
                      </div>
                    ) : null}
                    {result.weightedOverall != null ? (
                      <div data-score="feedback">
                        <Sub>{t('performance.workspace.me.result.feedbackScore')}</Sub>
                        <ScoreValue>{`${formatNumber(result.weightedOverall, lang)} / 5`}</ScoreValue>
                      </div>
                    ) : null}
                  </ScoreRow>
                  <Explain>{t('performance.workspace.me.result.explanation')}</Explain>
                </>
              ) : (
                <Explain data-state="no-scoring">{t('performance.workspace.me.result.noScoring')}</Explain>
              )}
              <AcknowledgementBanner acknowledgement={result.acknowledgement} onAcknowledge={handleAcknowledge} />
              <Actions>
                {result.objectionWindowOpen && result.objectionWindowEndsAt ? (
                  <Sub>{t('performance.workspace.me.result.objectionUntil', { date: formatDate(result.objectionWindowEndsAt, lang) })}</Sub>
                ) : <span />}
                {result.objectionWindowOpen ? (
                  <Button variant="outline" onClick={() => open('results', { period: selectedPeriodId })} data-action="objection">
                    {t('performance.workspace.me.result.objection')}
                  </Button>
                ) : null}
              </Actions>
            </div>
          ) : null}
          <Footer>
            <Button variant="ghost" onClick={() => open('results')}>{t('performance.workspace.me.result.open')}</Button>
          </Footer>
        </PanelCard>
      </SummaryGrid>
    </div>
  );
}
